import { DatesRangeValue } from '@mantine/dates';
import moment from 'moment';
import {
  FEEDBACK_RATING_DOWN,
  FEEDBACK_RATING_UP,
  FEEDBACK_REASONS,
} from 'src/app/chat/messageFeedback';
import { formatDate } from 'src/utils';

export const AI_ASSISTANT_PATH = '/settings/ai-assistant';

export const AI_ASSISTANT_TABS = [
  'overview',
  'conversations',
  'feedback',
  'knowledge',
  'sql-audit',
  'usage',
] as const;

export type AIAssistantTab = typeof AI_ASSISTANT_TABS[number];

export const DEFAULT_AI_ASSISTANT_TAB: AIAssistantTab = 'overview';

export type FeedbackRating =
  | typeof FEEDBACK_RATING_UP
  | typeof FEEDBACK_RATING_DOWN;

export interface IFeedbackUrlFilters {
  /** Absent when the URL carries no dates, so the caller can apply its default range. */
  dateRange?: DatesRangeValue;
  rating?: FeedbackRating;
  reasons: string[];
  userId?: number;
}

const TAB_PARAM = 'tab';
const DATE_FROM_PARAM = 'dateFrom';
const DATE_TO_PARAM = 'dateTo';
const RATING_PARAM = 'rating';
const REASONS_PARAM = 'reasons';
const USER_PARAM = 'userId';
const URL_DATE_FORMAT = 'YYYY-MM-DD';

const KNOWN_REASONS = new Set(FEEDBACK_REASONS.map((r) => r.value));

export const isAIAssistantTab = (
  value: string | null,
): value is AIAssistantTab =>
  (AI_ASSISTANT_TABS as readonly string[]).includes(value ?? '');

const isRating = (value: string | null): value is FeedbackRating =>
  value === FEEDBACK_RATING_UP || value === FEEDBACK_RATING_DOWN;

const parseUrlDate = (value: string | null): Date | null => {
  if (!value) return null;
  const parsed = moment(value, URL_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const readAIAssistantTab = (params: URLSearchParams): AIAssistantTab => {
  const value = params.get(TAB_PARAM);
  return isAIAssistantTab(value) ? value : DEFAULT_AI_ASSISTANT_TAB;
};

/** The default tab clears the param so bare links stay canonical. */
export const writeAIAssistantTab = (
  params: URLSearchParams,
  tab: AIAssistantTab,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (tab === DEFAULT_AI_ASSISTANT_TAB) next.delete(TAB_PARAM);
  else next.set(TAB_PARAM, tab);
  return next;
};

/** Reasons only exist on "down" votes, so any reason implies that rating and a helpful-only filter carries none. */
const applyReasonRule = (
  rating: FeedbackRating | undefined,
  reasons: string[],
): Pick<IFeedbackUrlFilters, 'rating' | 'reasons'> => {
  if (rating === FEEDBACK_RATING_UP) return { rating, reasons: [] };
  if (reasons.length > 0) return { rating: FEEDBACK_RATING_DOWN, reasons };
  return { rating, reasons };
};

/** Unknown ratings and reason codes are dropped: the API answers 400 for them. */
export const readFeedbackFilters = (
  params: URLSearchParams,
): IFeedbackUrlFilters => {
  const rawRating = params.get(RATING_PARAM);
  const { rating, reasons } = applyReasonRule(
    isRating(rawRating) ? rawRating : undefined,
    (params.get(REASONS_PARAM) ?? '')
      .split(',')
      .filter((reason) => KNOWN_REASONS.has(reason)),
  );
  const userId = Number(params.get(USER_PARAM));
  const from = parseUrlDate(params.get(DATE_FROM_PARAM));
  const to = parseUrlDate(params.get(DATE_TO_PARAM));

  return {
    dateRange: from || to ? [from, to] : undefined,
    rating,
    reasons,
    userId: Number.isInteger(userId) && userId > 0 ? userId : undefined,
  };
};

/** Params other than the feedback filters (the tab) are kept. */
export const writeFeedbackFilters = (
  params: URLSearchParams,
  filters: IFeedbackUrlFilters,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const setOrDelete = (key: string, value: string | undefined) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  const [from, to] = filters.dateRange ?? [null, null];
  const { rating, reasons } = applyReasonRule(filters.rating, filters.reasons);

  setOrDelete(DATE_FROM_PARAM, formatDate(from, URL_DATE_FORMAT) || undefined);
  setOrDelete(DATE_TO_PARAM, formatDate(to, URL_DATE_FORMAT) || undefined);
  setOrDelete(RATING_PARAM, rating);
  setOrDelete(REASONS_PARAM, reasons.join(','));
  setOrDelete(USER_PARAM, filters.userId ? String(filters.userId) : undefined);
  return next;
};

export const feedbackTabHref = (
  filters: Partial<IFeedbackUrlFilters>,
): string => {
  const params = writeFeedbackFilters(
    writeAIAssistantTab(new URLSearchParams(), 'feedback'),
    { reasons: [], ...filters },
  );
  return `${AI_ASSISTANT_PATH}?${params.toString()}`;
};
